const CHANNEL = 'edusense-penpot';
const VERSION = 1;

export function configuredPenpotOrigin(location = window.location, document = window.document) {
  const configured = document.querySelector('meta[name="edusense-penpot-origin"]')?.content;
  if (configured) {
    const url = new URL(configured);
    if (url.origin !== configured || !['http:', 'https:'].includes(url.protocol)) throw new Error('Penpot 서버 주소 설정을 확인해 주세요.');
    return url.origin;
  }
  if (['127.0.0.1', 'localhost', '[::1]'].includes(location.hostname)) return 'http://127.0.0.1:39101';
  return null;
}

export function penpotWorkspaceUrl(origin, source, manifestUrl) {
  const url = new URL(origin);
  if (source && source.origin !== origin) throw new Error('이 디자인의 원본 서버가 현재 연결된 Penpot 서버와 다릅니다. 원본 서버 설정을 확인해 주세요.');
  const query = new URLSearchParams();
  if (source) { query.set('team-id', source.teamId); query.set('file-id', source.fileId); query.set('page-id', source.pageId); }
  if (manifestUrl) query.set('plugin', manifestUrl);
  // Penpot's dashboard route requires a team id. Its root resolves the signed-in
  // profile's default team; a synthetic /dashboard route displays a 404.
  url.hash = source ? `/workspace?${query}` : '';
  return url.href;
}

/** The entire original workspace owns its state and panels. No editor controls are reimplemented. */
export function mountPenpotNative(host, { source, onImport, onStatus, onError }) {
  let origin = null, connectionError = null;
  try { origin = configuredPenpotOrigin(); } catch (error) { connectionError = error; }
  const wrapper = document.createElement('div');
  wrapper.className = 'es-penpot-native';
  const help = document.createElement('details');
  help.className = 'es-penpot-help';
  const summary = document.createElement('summary');
  summary.textContent = 'Penpot 원본 편집기 · 콘텐츠에 반영하는 방법';
  const note = document.createElement('p');
  note.textContent = '레이어·에셋·속성·컴포넌트·레이아웃은 Penpot에서 편집합니다. 보드를 선택한 뒤 EduSense 플러그인에서 보내면 독자용 이미지가 갱신됩니다. 편집 원본은 Penpot 서버에 자동 저장되며, 콘텐츠 파일에는 원본 위치와 마지막으로 보낸 이미지가 저장됩니다.';
  const manifestUrl = new URL('/penpot-bridge/manifest.json', origin || window.location.origin).href;
  const plugin = document.createElement('input');
  plugin.readOnly = true; plugin.value = manifestUrl;
  plugin.setAttribute('aria-label', 'EduSense Penpot 플러그인 주소');
  plugin.addEventListener('focus', () => plugin.select());
  const install = document.createElement('p');
  install.textContent = '최초 연결: Penpot의 플러그인 관리자에서 아래 주소로 설치한 후 EduSense 플러그인을 실행하세요. 편집 원본 전체를 옮기려면 Penpot의 파일 내보내기를 사용하세요.';
  help.append(summary, note, install, plugin);
  wrapper.append(help); host.append(wrapper);
  let workspaceUrl;
  try { if (origin) workspaceUrl = penpotWorkspaceUrl(origin, source, manifestUrl); } catch (error) { connectionError = error; }
  if (!origin || connectionError) {
    help.open = true;
    const error = document.createElement('p'); error.setAttribute('role', 'alert');
    error.textContent = connectionError ? connectionError.message : 'Penpot 서버가 설정되지 않았습니다. 서버를 연결하면 원본 편집기를 사용할 수 있습니다.';
    wrapper.append(error);
    onStatus('원본 서버 연결 필요 · 저장된 디자인은 독자 보기에서 확인하세요');
    return { getStatus() { return { origin, connected: false, selection: null }; }, dispose() { wrapper.remove(); } };
  }
  const frame = document.createElement('iframe');
  frame.title = 'Penpot 원본 디자인 편집기';
  frame.dataset.testid = 'studio-penpot-frame';
  frame.allow = 'clipboard-read; clipboard-write; fullscreen';
  frame.src = workspaceUrl;
  wrapper.append(frame);
  let peer = null, sessionToken = null, disposed = false, importing = false, ready = false, selection = null, pendingExport = null;
  const received = new Set();
  const send = value => peer?.postMessage({ channel: CHANNEL, version: VERSION, sessionToken, ...value }, origin);
  function rejectPending(error) {
    if (!pendingExport) return;
    clearTimeout(pendingExport.timer); received.add(pendingExport.id); pendingExport.reject(error); pendingExport = null;
  }
  const receive = async event => {
    const message = event.data;
    if (disposed || event.origin !== origin || !message || message.channel !== CHANNEL || message.version !== VERSION) return;
    if (message.type === 'hello') {
      // Only the plugin UI directly hosted by this Penpot frame can establish a session.
      if (!event.source || event.source === window || typeof message.nonce !== 'string' || message.nonce.length > 200) return;
      try { if (event.source.parent !== frame.contentWindow) return; } catch { return; }
      if (peer !== event.source) {
        rejectPending(Object.assign(new Error('Penpot 플러그인 연결이 변경되었습니다.'), { code: 'PENPOT_DISCONNECTED' }));
        peer = event.source; sessionToken = crypto.randomUUID(); received.clear(); ready = false; selection = null;
      }
      send({ type: 'init', nonce: message.nonce }); return;
    }
    if (event.source !== peer || !sessionToken || message.sessionToken !== sessionToken) return;
    if (message.type === 'ready') { ready = true; onStatus('Penpot 연결됨 · 보드를 선택해 콘텐츠로 보내세요'); return; }
    if (message.type === 'selection') { selection = structuredClone(message.payload); return; }
    if (message.type === 'error') {
      const error = Object.assign(new Error(message.error?.message || 'Penpot에서 디자인을 가져오지 못했습니다.'), { code: message.error?.code || 'PENPOT_EXPORT_FAILED' });
      if (message.requestId === pendingExport?.id) rejectPending(error);
      onError(error); return;
    }
    if (message.type !== 'exported') return;
    const requestId = message.requestId;
    if (typeof requestId !== 'string' || requestId.length > 200 || received.has(requestId)) return;
    if (importing) { send({ type: 'error', requestId, error: { code: 'BUSY', message: '앞선 디자인을 반영하는 중입니다. 잠시 뒤 다시 보내 주세요.' } }); return; }
    importing = true;
    try {
      if (message.payload?.native?.serverOrigin !== origin) throw new Error('연결된 Penpot 서버에서 보낸 디자인만 받을 수 있습니다.');
      await onImport(message.payload, { requestId });
      if (!disposed) {
        received.add(requestId); send({ type: 'imported', requestId });
        if (pendingExport?.id === requestId) {
          clearTimeout(pendingExport.timer);
          pendingExport.resolve({ native: message.payload.native, width: message.payload.image.width, height: message.payload.image.height }); pendingExport = null;
        }
      }
    } catch (error) {
      if (pendingExport?.id === requestId) rejectPending(error);
      if (!disposed) { onError(error); send({ type: 'error', requestId, error: { code: 'IMPORT_FAILED', message: error.message } }); }
    } finally { importing = false; }
  };
  window.addEventListener('message', receive);
  frame.addEventListener('load', () => {
    rejectPending(Object.assign(new Error('Penpot 원본 화면이 다시 열렸습니다.'), { code: 'PENPOT_DISCONNECTED' }));
    peer = null; sessionToken = null; ready = false; selection = null;
    onStatus('Penpot 원본 화면 · 디자인은 Penpot 서버에 저장됩니다');
  });
  return {
    getStatus() { return { origin, connected: ready && peer !== null, selection: structuredClone(selection) }; },
    requestExport({ requestId, timeoutMs = 120000 }) {
      if (disposed || !peer || !ready) return Promise.reject(Object.assign(new Error('Penpot의 EduSense 플러그인을 먼저 연결하세요.'), { code: 'PENPOT_NOT_READY' }));
      if (!/^[a-zA-Z0-9_-]{1,100}$/.test(requestId)) return Promise.reject(new Error('유효한 내보내기 requestId가 필요합니다.'));
      if (pendingExport || importing || received.has(requestId)) return Promise.reject(Object.assign(new Error('내보내기 요청이 이미 처리되었거나 진행 중입니다.'), { code: 'BUSY' }));
      return new Promise((resolve, reject) => {
        pendingExport = { id: requestId, resolve, reject, timer: setTimeout(() => {
          send({ type: 'error', requestId, error: { code: 'TIMEOUT', message: '보드 연결 시간이 초과되었습니다.' } });
          rejectPending(Object.assign(new Error('보드 연결 시간이 초과되었습니다. Penpot 플러그인을 확인하세요.'), { code: 'TIMEOUT' }));
        }, timeoutMs) };
        send({ type: 'export', requestId });
      });
    },
    dispose() {
      disposed = true; rejectPending(Object.assign(new Error('Penpot 페이지가 닫혔습니다.'), { code: 'PENPOT_DISCONNECTED' }));
      window.removeEventListener('message', receive); peer = null; wrapper.remove();
    },
  };
}
